import { useQuery, UseQueryOptions } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';

type Args = {
  integrationId: number;
};

type TokenResponse = {
  key: string;
  user: number;
  user_email?: string;
  created?: string;
};

export type IntegrationToken = {
  token: string | null;
  userId: number | null;
};

const QUERY_KEY_PREFIX = 'query-integration-token';
const getQueryKey = (args: Args) => `${QUERY_KEY_PREFIX}-${args.integrationId}`;

function useQueryIntegrationToken(
  args: Args,
  options?: UseQueryOptions<IntegrationToken, Error, IntegrationToken>
) {
  const { integrationId } = args;
  const notify = useNotify();

  const queryKey = getQueryKey(args);
  const email = `app_${integrationId}@zapierpartner.com`;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Call z/z proxy endpoint: GET /api/platform/cli/apps/{app_id}/tokens/<email>/
      const tokenUrl = new URL(
        `/api/platform/cli/apps/${encodeURIComponent(
          integrationId
        )}/tokens/${encodeURIComponent(email)}/`,
        ZAPIER_APP_BASE_URL
      );

      try {
        const response = (await fetchJson(tokenUrl.href)) as TokenResponse;
        return {
          token: response.key || null,
          userId: response.user || null,
        };
      } catch (error) {
        // Token doesn't exist yet (integration not in beta/public)
        if (error.status === 404) {
          return { token: null, userId: null };
        }
        throw error;
      }
    },
    onError: async (error: any) => {
      // Only notify on unexpected errors (not 404s)
      if (error.status !== 404) {
        notify.failure(error.message, queryKey);
      }
    },
    ...options,
  });

  return { ...query, queryKey };
}

export default useQueryIntegrationToken;
export { getQueryKey, QUERY_KEY_PREFIX };
